<template>
  <div class="swiper-main-container">
    <UiSlider
      :key="sliderKey"
      :slides="slides"
      :options="sliderOptions"
      :space-between="16"
      arrows-always-visible
    >
      <template #slide="{ slide }">
        <ExpCardRedesign
          :exp="slide"
          size="s"
          :is-in-favorites="isFavorite(slide.id)"
          :tooltip-teleport-target="modalTarget"
          style="width: 100%"
          @toggle-favorite="toggleFavorite"
        />
      </template>
      <template #rightArrowIcon>
        <UiBaseIcon
          width="24"
          height="24"
          viewBox="0 0 16 16"
        >
          <path
            d="M5.52536 12.3965C5.23434 12.6905 5.23434 13.164 5.52536 13.4581C5.82067 13.7565 6.30272 13.7565 6.59803 13.4581L11.3038 8.70344C11.6895 8.31377 11.6895 7.68623 11.3038 7.29656L6.59803 2.54191C6.30272 2.24353 5.82067 2.24353 5.52536 2.54191C5.23434 2.83595 5.23434 3.3095 5.52536 3.60355L9.87661 8L5.52536 12.3965Z"
          />
        </UiBaseIcon>
      </template>
      <template #leftArrowIcon>
        <UiBaseIcon
          width="24"
          height="24"
          viewBox="0 0 16 16"
          class="mirror"
        >
          <path
            d="M5.52536 12.3965C5.23434 12.6905 5.23434 13.164 5.52536 13.4581C5.82067 13.7565 6.30272 13.7565 6.59803 13.4581L11.3038 8.70344C11.6895 8.31377 11.6895 7.68623 11.3038 7.29656L6.59803 2.54191C6.30272 2.24353 5.82067 2.24353 5.52536 2.54191C5.23434 2.83595 5.23434 3.3095 5.52536 3.60355L9.87661 8L5.52536 12.3965Z"
          />
        </UiBaseIcon>
      </template>
    </UiSlider>
  </div>
</template>

<script setup lang="ts">
  import { ExpCardRedesign } from '@experience/exp-card';
  import { UiSlider, UiBaseIcon } from '@experience/vue3-kit';
  import { computed, inject, onMounted, watch } from 'vue';

  import { useFavorites } from '@/shared/composables/useFavorites';
  import { useMovementTypes } from '@/shared/composables/useMovementTypes';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  import type { Experience } from '@experience/exp-card/dist/types/experience';

  const props = defineProps<{
    experiences?: Experience[];
  }>();

  const { isFavorite, seedFavorites, toggleFavorite } = useFavorites();
  const { loadMovementTypes, localizeExperienceMovement } = useMovementTypes();

  onMounted(loadMovementTypes);

  watch(
    () => props.experiences,
    (list) => {
      if (list?.length) {
        seedFavorites(list);
      }
    },
    { immediate: true },
  );

  const slides = computed<Experience[]>(() => {
    const list = props.experiences ?? [];

    return list.map((exp) => localizeExperienceMovement(exp));
  });

  // Пересоздаём слайдер при смене набора экскурсий (переключение таба рубрик),
  // чтобы позиция скролла сбрасывалась на первый слайд.
  const sliderKey = computed(() => slides.value.map((exp) => exp.id).join('-'));

  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);
  const modalTarget = shadowRoot?.querySelector<HTMLElement>('.modal-target') ?? undefined;

  const getSlideConfig = (slidesPerView: number, spaceBetween: number, slidesPerGroup = 1) => ({
    slidesPerView,
    spaceBetween,
    slidesPerGroup,
  });

  const { isTablet, isMobile } = useScreenSize();

  const sliderOffset = computed(() => {
    if (isTablet.value && !isMobile.value) {
      return 20;
    }

    if (isMobile.value) {
      return 24;
    }

    return 0;
  });

  const sliderOptions = computed(() => ({
    spaceBetween: 16,
    breakpoints: {
      320: getSlideConfig(1, 16),
      371: getSlideConfig(1.15, 16),
      401: getSlideConfig(1.25, 16),
      481: getSlideConfig(1.5, 16),
      601: getSlideConfig(2, 16),
      801: getSlideConfig(2.5, 16),
      921: getSlideConfig(3, 16, 3),
      1081: getSlideConfig(4, 16, 4),
    },
    slidesOffsetAfter: sliderOffset.value,
    slidesOffsetBefore: sliderOffset.value,
  }));
</script>

<style lang="scss">
  .exp-snippet {
    width: 100%;
    max-width: 100% !important;
  }

  .mirror {
    transform: scaleX(-1);
  }

  .btn-next,
  .btn-prev {
    top: 18% !important;

    @include tablet {
      display: none !important;
    }
  }

  .btn-prev {
    transform: translateX(-50%) !important;
  }

  .exp-snippet .exp-pic-outer,
  .exp-snippet .exp-pic-outer .exp-pic,
  .exp-snippet .exp-pic-outer .exp-pic .image,
  .exp-snippet .exp-pic-outer .exp-pic-gradients {
    border-radius: 8px;
  }

  .swiper-main-container {
    min-width: 1260px;
    max-width: 950px;
    padding: 0 var(--alias-space-20, 20px);
    margin: var(--alias-space-24, 24px) auto 0;

    .swiper-slide {
      align-self: flex-start;
      height: auto;
    }

    @include breakpoint(tablet) {
      box-sizing: border-box;
      min-width: 100%;
      max-width: 100%;
      padding: 0;
      margin: var(--alias-space-20, 20px) auto 0;
    }
  }
</style>
